import { BElement } from "utils/base-component"

export class MobileMenu {
  private cssClass: string
  private menuBtn: BElement
  private menuNav: BElement
  private favouritesBtn: BElement
  private favouritesNav: BElement
  private searchBtn: BElement
  private searchForm: BElement

  constructor(cssClass: string) {
    this.cssClass = cssClass
    this.initializeElements()
    this.addEventListeners()
  }

  private initializeElements() {
    const mobileMenuBtn = document.querySelector(".mobile-header__menu-button")
    this.menuBtn = new BElement(mobileMenuBtn, this.cssClass)

    const mobileMenuNav = document.querySelector(".menu-nav")
    this.menuNav = new BElement(mobileMenuNav, this.cssClass)

    const mobileFavouritesBtn = document.querySelector(".mobile-header__favourites-button")
    this.favouritesBtn = new BElement(mobileFavouritesBtn, this.cssClass)

    const mobileFavouritesNav = document.querySelector(".favourites-dropdown")
    this.favouritesNav = new BElement(mobileFavouritesNav, this.cssClass)

    const searchBtn = document.querySelector(".dropdown-search-combo__mobile-search")
    this.searchBtn = new BElement(searchBtn, this.cssClass)

    const searchForm = document.querySelector(".mobile-header__search-form")
    this.searchForm = new BElement(searchForm, this.cssClass)
  }

  private addEventListeners() {
    this.menuBtn.element.addEventListener("click", (e) => {
      e.preventDefault()
      this.toggleMenu(this.menuNav, "block", this.menuBtn)
    })

    this.favouritesBtn.element.addEventListener("click", (e) => {
      e.preventDefault()
      this.favouritesBtn.element.classList.add("animate")
      this.toggleMenu(this.favouritesNav, "block", this.favouritesBtn)
    })

    this.searchBtn.element.addEventListener("click", (e) => {
      e.preventDefault()
      this.toggleMenu(this.searchForm, "flex", this.searchBtn)
    })
  }

  // Methods to control sticky footer ad visibility
  private static hideStickyAd(): void {
    const stickyAd = document.querySelector("#adb-adhesion-container-1")
    if (stickyAd instanceof HTMLElement) {
      stickyAd.style.display = "none"
    }
  }

  private static showStickyAd(): void {
    const stickyAd = document.querySelector("#adb-adhesion-container-1")
    if (stickyAd instanceof HTMLElement) {
      stickyAd.style.display = ""
    }
  }

  // Helper method to toggle a specific menu
  private toggleMenu(menu: BElement, displayType: string = "block", button?: BElement): void {
    if (!menu || !menu.element) return

    const menuElement = menu.element
    const currentDisplay = menu.element.style.display
    // Check if this menu is already visible
    const isVisible = currentDisplay !== "none" && currentDisplay !== ""

    // For all other menus
    if (isVisible) {
      // Simply hide this menu if it's already visible
      menuElement.style.display = "none"
      // Remove active class from menu if present
      if (menuElement.classList.contains("active")) {
        menuElement.classList.remove("active")
      }
      // Allow body scrolling when menu is hidden
      document.body.style.overflow = ""
      // Show sticky ad when menu is closed
      if (menuElement.classList.contains("menu-nav")) {
        MobileMenu.showStickyAd()
      }
      // Also toggle aria-expanded state on buttons if present
      if (button && button.element) {
        // Set aria-expanded to false
        if (button.element.hasAttribute("aria-expanded")) {
          button.element.setAttribute("aria-expanded", "false")
        }
        // Remove active class from button if present
        if (button.element.classList.contains("active")) {
          button.element.classList.remove("active")
        }
      }
    } else {
      // Close all menus first
      this.closeAllMobileMenus()
      // Then show this menu
      menuElement.style.display = displayType
      // Add active class to menu
      menuElement.classList.add("active")
      // Prevent body scrolling when full-screen menu is shown
      // Only apply to full-screen menu (nav containers)
      if (menuElement.classList.contains("menu-nav")) {
        document.body.style.overflow = "hidden"
        // Hide sticky ad when mobile menu is active
        MobileMenu.hideStickyAd()
      }
      // Also toggle the button states if provided
      if (button && button.element) {
        // Set aria-expanded to true if the attribute exists
        if (button.element.hasAttribute("aria-expanded")) {
          button.element.setAttribute("aria-expanded", "true")
        }
        // Add active class to button
        button.element.classList.add("active")
      }
    }
  }

  // Helper to close all mobile menus with an optional exception
  closeAllMobileMenus(exceptMenu?: BElement): void {
    const menus = [this.menuNav, this.favouritesNav, this.searchForm]
    // flag to track if a fullscreen menu is open
    let menuOpen = false
    // create a mapping between menus and their toggle buttons
    const menuButtonMap = new Map([
      [this.menuNav, this.menuBtn],
      [this.favouritesNav, this.favouritesBtn],
      [this.searchForm, this.searchBtn],
    ])

    menus.forEach((menu) => {
      // Skip this menu if it's the exception
      if (exceptMenu && menu === exceptMenu) {
        // If the exception is a full-screen menu and it will remain open, set the flag to true
        if (menu.element && menu.element.classList.contains("menu-nav")) {
          menuOpen = true
        }
        return
      }

      if (menu && menu.element) {
        const menuElement = menu.element
        menuElement.style.display = "none"

        // Remove active class if present
        if (menuElement.classList.contains("active")) {
          menuElement.classList.remove("active")
        }

        // Get the corresponding button for this menu
        const button = menuButtonMap.get(menu)

        // Reset aria-expanded and active class for the button if applicable
        if (button && button.element) {
          // Reset aria-expanded attribute if it exists
          if (button.element.hasAttribute("aria-expanded")) {
            button.element.setAttribute("aria-expanded", "false")
          }

          // Remove active class from button if present
          if (button.element.classList.contains("active")) {
            button.element.classList.remove("active")
          }
        }
      }
    })

    // If no full-screen menu is open, restore body scrolling
    if (!menuOpen) {
      document.body.style.overflow = ""
      // Show sticky ad when all menus are closed
      MobileMenu.showStickyAd()
    }
  }

  // Public method to check if click is inside menus (for document click handler)
  isMenuClick(target: Node): boolean {
    return (
      (this.menuNav.element && this.menuNav.element.contains(target)) ||
      (this.favouritesNav.element && this.favouritesNav.element.contains(target)) ||
      (this.searchForm.element && this.searchForm.element.contains(target)) ||
      (this.searchBtn.element && this.searchBtn.element.contains(target))
    )
  }

  // Public method to check if click is on menu buttons (for document click handler)
  isMenuButtonClick(target: Node): boolean {
    return (
      (this.menuBtn.element && this.menuBtn.element.contains(target)) ||
      (this.favouritesBtn.element && this.favouritesBtn.element.contains(target)) ||
      (this.searchBtn.element && this.searchBtn.element.contains(target))
    )
  }

  isSearchButtonClick(e: Event): boolean {
    if (
      e.target instanceof Element &&
      (e.target.classList.contains("dropdown-search-combo__mobile-search") ||
        e.target.closest(".dropdown-search-combo__mobile-search"))
    ) {
      e.preventDefault()
      // Handle the search button click - toggle the mobile search
      this.toggleMenu(this.searchForm, "flex", this.searchBtn)
      return true // Indicates this event was handled
    }
    return false // Event was not handled by this method
  }
}
